import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/navigation/Sidebar';
import PatientContextBar from '../../components/navigation/PatientContextBar';
import ClinicalAlerts from '../../components/navigation/ClinicalAlerts';
import QuickActions from '../../components/navigation/QuickActions';
import SessionManager from '../../components/navigation/SessionManager';
import PatientInfoCard from './components/PatientInfoCard';
import DentalChartCard from './components/DentalChartCard';
import TreatmentPlansCard from './components/TreatmentPlansCard';
import AllergiesCard from './components/AllergiesCard';
import UpcomingAppointments from './components/UpcomingAppointments';
import QuickActionsPanel from './components/QuickActionsPanel';
import PatientSearchBar from './components/PatientSearchBar';
import Icon from '../../components/AppIcon';

const PatientDashboard = () => {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [dentalConditions, setDentalConditions] = useState([
    {
      id: 1,
      code: 'D0150',
      description: 'Comprehensive oral evaluation',
      category: 'Diagnostic',
      status: 'Completed',
      completedDate: '2026-01-10'
    },
    {
      id: 2,
      code: 'D2391',
      description: 'Resin-based composite - one surface, posterior',
      category: 'Restorative',
      status: 'Scheduled',
      scheduledDate: '2026-01-25'
    }
  ]);

  const currentPatient = {
    name: "Sarah Elizabeth Johnson",
    mrn: "MRN-2024-789456",
    dob: "03/15/1978",
    age: "47",
    gender: "Female",
    phone: "(555) 234-5678",
    email: "sarah.johnson@email.com",
    status: "Active",
    primaryProvider: "Dr. Michael Chen, DDS",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b72fa019-1763294606942.png",
    avatarAlt: "Professional headshot of middle-aged woman with shoulder-length brown hair wearing blue medical scrubs and warm smile"
  };

  const [clinicalAlerts, setClinicalAlerts] = useState([
    {
      id: 1,
      severity: "critical",
      title: "Antibiotic Premedication Required",
      message: "Patient has history of heart valve replacement. Antibiotic prophylaxis required before dental procedures.",
      actionRequired: "Prescribe amoxicillin 2g 1 hour before procedure",
      timestamp: "2 min ago",
      dismissible: true
    },
    {
      id: 2,
      severity: "warning",
      title: "Overdue Cleaning",
      message: "Patient\'s last prophylaxis was 8 months ago. Recommend scheduling cleaning appointment.",
      actionRequired: "Schedule hygiene appointment",
      timestamp: "1 hour ago",
      dismissible: true
    },
    {
      id: 3,
      severity: "info",
      title: "X-rays Available",
      message: "Bitewing radiographs from 01/12/2026 are ready for review.",
      timestamp: "3 hours ago",
      dismissible: true
    }
  ]);

  const dentalChart = {
    lastUpdated: "Today, 09:30 AM",
    conditions: [
      {
        area: "Tooth #3",
        condition: "Caries",
        status: "treatment",
        priority: "high",
        notes: "Moderate decay, needs filling"
      },
      {
        area: "Tooth #14",
        condition: "Crown",
        status: "healthy",
        notes: "Porcelain crown, good condition"
      },
      {
        area: "Tooth #19",
        condition: "Watch",
        status: "watch",
        priority: "medium",
        notes: "Small cavity forming, monitor"
      },
      {
        area: "Tooth #30",
        condition: "Missing",
        status: "missing",
        notes: "Extracted 2020, implant candidate"
      },
      {
        area: "Periodontal",
        condition: "Mild Gingivitis",
        status: "watch",
        notes: "Improve oral hygiene, 3mm pockets"
      },
      {
        area: "Overall",
        condition: "Good",
        status: "healthy",
        notes: "Regular maintenance recommended"
      }
    ]
  };

  const currentTreatments = [
    {
      procedure: "Composite Filling",
      tooth: "Tooth #3 - Occlusal surface",
      status: "scheduled",
      date: "Jan 25, 2026",
      dentist: "Dr. Michael Chen",
      cost: "185.00"
    },
    {
      procedure: "Dental Crown",
      tooth: "Tooth #14 - Full coverage",
      status: "completed",
      date: "Dec 10, 2025",
      dentist: "Dr. Michael Chen",
      cost: "1,250.00"
    },
    {
      procedure: "Prophylaxis (Cleaning)",
      tooth: "Full mouth",
      status: "in-progress",
      date: "Today",
      dentist: "Sarah Williams, RDH",
      cost: "125.00"
    },
    {
      procedure: "Dental Implant Consultation",
      tooth: "Tooth #30 site",
      status: "proposed",
      date: "Feb 15, 2026",
      dentist: "Dr. Emily Rodriguez",
      cost: "3,500.00"
    }
  ];

  const activeDentalAllergies = [
    {
      allergen: "Penicillin",
      severity: "Severe",
      reaction: "Anaphylaxis - severe allergic reaction with difficulty breathing and hives",
      documentedDate: "05/12/2015"
    },
    {
      allergen: "Latex",
      severity: "Moderate",
      reaction: "Contact dermatitis with skin rash and itching",
      documentedDate: "08/22/2018"
    },
    {
      allergen: "Lidocaine",
      severity: "Mild",
      reaction: "Mild tachycardia and anxiety",
      documentedDate: "03/10/2022"
    }
  ];

  const clinicalActivities = [
    {
      type: "appointment",
      title: "Prophylaxis and Examination",
      description: "Routine cleaning completed. Patient reports no pain or sensitivity. Oral hygiene good.",
      time: "Today, 09:00 AM",
      provider: "Sarah Williams, RDH",
      details: true
    },
    {
      type: "imaging",
      title: "Bitewing Radiographs",
      description: "Four bitewing X-rays taken. Caries detected on tooth #3. No other abnormalities noted.",
      time: "Yesterday, 02:30 PM",
      provider: "Dental Imaging Department",
      details: true
    },
    {
      type: "treatment",
      title: "Crown Cementation",
      description: "Porcelain crown cemented on tooth #14. Patient tolerated procedure well. No complications.",
      time: "Dec 10, 2025",
      provider: "Dr. Michael Chen, DDS",
      details: true
    },
    {
      type: "note",
      title: "Follow-up Phone Call",
      description: "Patient called regarding post-crown sensitivity. Advised normal healing process, monitor for 2 weeks.",
      time: "Dec 12, 2025",
      provider: "Front Desk Staff",
      details: false
    },
    {
      type: "procedure",
      title: "Periodontal Probing",
      description: "Full mouth periodontal charting completed. Mild gingivitis noted. 3mm pockets generalized.",
      time: "Nov 15, 2025",
      provider: "Sarah Williams, RDH",
      details: true
    }
  ];

  const upcomingAppointments = [
    {
      type: "Restorative - Filling",
      date: "Jan 25, 2026",
      time: "10:30 AM",
      provider: "Dr. Michael Chen, DDS",
      location: "Operatory 2"
    },
    {
      type: "Implant Consultation",
      date: "Feb 15, 2026",
      time: "02:00 PM",
      provider: "Dr. Emily Rodriguez, DDS",
      location: "Consultation Room"
    },
    {
      type: "6-Month Cleaning",
      date: "Jul 10, 2026",
      time: "09:00 AM",
      provider: "Sarah Williams, RDH",
      location: "Hygiene Bay 1"
    }
  ];

  const recentPatients = [
    { name: "Sarah Elizabeth Johnson", mrn: "MRN-2024-789456", dob: "03/15/1978" },
    { name: "Robert Michael Davis", mrn: "MRN-2024-654321", dob: "07/22/1965" },
    { name: "Jennifer Anne Martinez", mrn: "MRN-2024-987654", dob: "11/08/1982" },
    { name: "William James Thompson", mrn: "MRN-2024-456789", dob: "05/30/1970" },
    { name: "Maria Elena Garcia", mrn: "MRN-2024-321654", dob: "09/14/1988" }
  ];

  const handleAlertClick = (alert) => {
    console.log('Alert clicked:', alert);
    // Navigate or show modal based on alert type
    if (alert?.severity === 'critical') {
      console.log('Critical Alert: ' + alert?.title + ' - ' + alert?.message);
    }
  };

  const handleDismissAlert = (alertId) => {
    setClinicalAlerts(prevAlerts => prevAlerts?.filter(alert => alert?.id !== alertId));
  };

  const handleQuickAction = (actionId) => {
    console.log('Quick action:', actionId);
    switch (actionId) {
      case 'schedule': navigate('/appointment-scheduling');
        break;
      case 'order-lab': navigate('/lab-results');
        break;
      case 'prescribe': navigate('/medication-management');
        break;
      case 'order-xray': navigate('/lab-results');
        break;
      case 'treatment-plan': navigate('/medication-management');
        break;
      case 'add-note': navigate('/clinical-documentation');
        break;
      case 'view-chart': navigate('/patient-records');
        break;
      case 'send-message': navigate('/patient-communication-hub');
        break;
      default:
        break;
    }
  };

  const handleSelectPatient = (patient) => {
    console.log('Selected patient:', patient);
  };

  const handleSessionExpired = () => {
    navigate('/login');
  };

  const handleSessionExtended = () => {
    console.log('Session extended');
  };

  return (
    <div className="min-h-screen bg-background pt-16">
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />

      <div className="">
        <PatientContextBar
          patient={currentPatient}
          alerts={clinicalAlerts}
          onAlertClick={handleAlertClick}
        />

        <main className="max-w-screen-2xl mx-auto px-4 md:px-6 py-6 md:py-8">
          <div className="mb-6 md:mb-8">
            <PatientSearchBar
              recentPatients={recentPatients}
              onSelectPatient={handleSelectPatient}
            />
          </div>

          {clinicalAlerts?.length > 0 && (
            <div className="mb-6 md:mb-8">
              <ClinicalAlerts
                alerts={clinicalAlerts}
                onAlertClick={handleAlertClick}
                onDismiss={handleDismissAlert}
              />
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <PatientInfoCard patient={currentPatient} />
              <DentalChartCard dentalChart={dentalChart} />
              <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="Stethoscope" size={20} />
                  Active Treatment Codes (CDT)
                </h3>
                <div className="space-y-2">
                  {dentalConditions?.filter(d => d?.status === 'Scheduled' || d?.status === 'Completed')?.map((condition) => (
                    <div key={condition?.id} className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg hover:bg-muted transition-smooth">
                      <Icon name="FileText" size={16} className="text-primary mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono font-semibold text-sm text-foreground">{condition?.code}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            condition?.status === 'Completed' ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'
                          }`}>
                            {condition?.status}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">{condition?.description}</p>
                        <p className="text-xs text-muted-foreground mt-1">Category: {condition?.category}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <TreatmentPlansCard
                treatments={currentTreatments}
                onViewAll={() => navigate('/medication-management')}
              />
              <AllergiesCard allergies={activeDentalAllergies} />
              <UpcomingAppointments
                appointments={upcomingAppointments}
                onScheduleNew={() => navigate('/appointment-scheduling')}
              />
            </div>

            <div className="space-y-4 md:space-y-6">
              <QuickActionsPanel onAction={handleQuickAction} />
            </div>
          </div>
        </main>
      </div>
      <QuickActions
        actions={[
          {
            id: 'order-xray',
            label: 'Order X-ray',
            icon: 'FlaskConical',
            onClick: () => navigate('/lab-results'),
            shortcut: 'Ctrl+X'
          },
          {
            id: 'treatment-plan',
            label: 'Treatment Plan',
            icon: 'Pill',
            onClick: () => navigate('/medication-management'),
            shortcut: 'Ctrl+T'
          },
          {
            id: 'schedule',
            label: 'Schedule',
            icon: 'Calendar',
            onClick: () => navigate('/appointment-scheduling'),
            shortcut: 'Ctrl+S'
          },
          {
            id: 'document',
            label: 'Document',
            icon: 'FileText',
            onClick: () => navigate('/clinical-documentation'),
            shortcut: 'Ctrl+D'
          }
        ]}
        position="bottom-right"
      />
      {showSessionWarning && (
        <SessionManager
          sessionTimeout={900000}
          warningTime={120000}
          onSessionExpired={handleSessionExpired}
          onSessionExtended={handleSessionExtended}
        />
      )}
    </div>
  );
};

export default PatientDashboard;